import * as React from "react";
import { ADJUSTMENT_CATEGORY_LOOKUP, ADJUSTMENT_STATUS_LOOKUP } from "../../../../lookup/Consts";
import { IAdjustmentState, IAdjustmentStateKeys } from "../../../../redux/types/IAdjustmentState";
import IRepertoireComponentDataItem from "../../../../redux/types/IRepertoireComponentDataItem";
import { SizedTextDataInput } from "../../../components/textDataInput/TextDataInput";
import { ADJUSTMENT_ADJUSTMENT_DISTRIBUTION_STATE_KEY, ADJUSTMENT_ADJUSTMENT_POOL_STATE_KEY, ADJUSTMENT_ADJUSTMENT_SEGMENT_STATE_KEY, ADJUSTMENT_BATCH_ID_STATE_KEY, ADJUSTMENT_CATEGORY_STATE_KEY, ADJUSTMENT_DATE_STATE_KEY, ADJUSTMENT_NARRATIVE_STATE_KEY, ADJUSTMENT_SOURCE_DISTRIBUTION_STATE_KEY, ADJUSTMENT_SOURCE_POOL_STATE_KEY, ADJUSTMENT_SOURCE_SEGMENT_STATE_KEY, ADJUSTMENT_STATUS_STATE_KEY, POST_ADJUSTMENT_BATCH_STATE_KEY, POST_AGREEMENT_BATCH_STATE_KEY } from "../../../Consts";
import { IRepertoireStateKeys } from "../../../types/IRepertoireStateKeys";
import { ILookupDictionary } from "../../../../lookup/types/ILookupDictionary";
import { ITabReduxItem } from "../../../../redux/types/ITabReduxItem";
import { SizedDropdownDataInput } from "../../../components/dropdownDataInput/DropdownDataInput";
import SizedDateDataInput from "../../../components/dateDataInput/DateDataInput";
import { DataSource } from "../../../types/DataSource";
import WorkBatchInput from "../../../works/components/workBatchInput/WorkBatchInput";
import { Dropdown, IDropdownOption } from 'office-ui-fabric-react/lib/Dropdown';
import { If } from "../../../../core/components/If";
import { POOL_ALL_KEY, POOL_ALL_LABEL } from "../../../../home/helpers/PoolSelectionHelper";
import {
    REPERTOIRE_PAGE_FORM_ITEM_SIZE
} from "../../../Consts";
import { getLookupValues } from "../../../../lookup/services/LookupHelpers";
import { isAdjustmentGeneralFieldMandatory } from "../../../utils/maintenanceMandatoryFields";

export interface IAdjustmentMaintenanceGeneralDataViewProps {
    adjustmentMaintenanceGeneralDataViewData: IRepertoireComponentDataItem;
    adjustment: IAdjustmentState,
    updateAdjustmentField: (value: any, fieldName: keyof IAdjustmentState) => void;
    activeTab: number;
    tabs: ITabReduxItem[];
    isReadonly: boolean;
    lookupValues: ILookupDictionary;
    sourceDistributions: { code: string; description: string }[];
    sourcePools: { code: string; description: string }[];
    sourceSegments: { code: string; description: string }[];
    adjustmentDistributions: { code: string; description: string }[];
    adjustmentPools: { code: string; description: string }[];
    adjustmentSegments: { code: string; description: string }[];
    changesMade: boolean;
    saveInProgress: boolean;
    adjustmentBatchOperationInProgress: boolean;
    postAdjustmentBatch: (adjustmentBatchId: number) => void
}

interface IAdjustmentMaintenanceGeneralDataViewState {
    loaded: boolean;
    selectedPools: any[];
}

const AdjustmentMaintenanceGeneralDataView: React.FC<IAdjustmentMaintenanceGeneralDataViewProps> = ({
    adjustmentMaintenanceGeneralDataViewData,
    adjustment,
    updateAdjustmentField,
    isReadonly,
    lookupValues,
    sourceDistributions,
    sourcePools,
    sourceSegments,
    adjustmentDistributions,
    adjustmentPools,
    adjustmentSegments,
    changesMade,
    saveInProgress,
    adjustmentBatchOperationInProgress,
    postAdjustmentBatch
}) => { 
    const [loaded, setLoaded] = React.useState(false);
    const [selectedPools, setSelectedPools] = React.useState<any[]>([]);
    const [viewData, setViewData] = React.useState({});

    const {
        adjustmentCategory,
        adjustmentDate,
        adjustmentDistribution,
        adjustmentPool,
        adjustmentSegment,
        adjustmentStatus,
        sourceDistribution,
        sourcePool,
        sourceSegment,
        narrative,
        batchId,
        dataSource
    } = adjustment;

    React.useEffect(() => {
        const fieldsLocal = adjustmentMaintenanceGeneralDataViewData?.fields;
        if (!fieldsLocal) return;

        const visibleFields = fieldsLocal.filter(field => !field.hidden);

        const viewDataLocal = visibleFields.reduce((acc, item) => {
            acc[item.name] = item.data;
            return acc;
        }, {} as Record<string, any>);

        setViewData(viewDataLocal);
        setLoaded(true);
    }, [adjustmentMaintenanceGeneralDataViewData]);

    React.useEffect(() => {
        setSelectedPools((sourcePool ?? []).filter(pool => typeof pool === "string" && pool !== ""));
    }, [sourcePool]);

    const changeData = (value: any, name: IRepertoireStateKeys) => {
        if (name === "sourceDistribution") {
            setSelectedPools([]);
        }
        updateAdjustmentField(value, name as IAdjustmentStateKeys);
    };

        const allPoolCodes = sourcePools.map(x => x.code);

        const multiSelectChangeData = (
            event: React.FormEvent<HTMLDivElement>,
            option?: IDropdownOption
        ) => {
            if (!option) return;

            if (option.key === POOL_ALL_KEY) {
                const allSelected = allPoolCodes.every(code =>
                    selectedPools.includes(code)
                );

                if (allSelected) {
                    setSelectedPools([]);
                    updateAdjustmentField([], "sourcePool");
                } else {
                    setSelectedPools(allPoolCodes);
                    updateAdjustmentField(allPoolCodes, "sourcePool");
                }
            } else {
                let updated: string[];

                if (selectedPools.includes(option.key as string)) {
                    // @ts-ignore
                    updated = selectedPools.filter(k => k !== option.key).filter(k => k !== POOL_ALL_KEY);
                } else {
                    // @ts-ignore
                    updated = [...selectedPools, option.key as string].filter(k => k !== POOL_ALL_KEY);
                }

                setSelectedPools(updated);
                updateAdjustmentField(updated, "sourcePool");
            }
        };


        const selectedPoolCodes = selectedPools.filter(pool => typeof pool === "string" && pool !== "");
        const allIndividualSelected = allPoolCodes.length > 0 &&
            allPoolCodes.every(code => selectedPoolCodes.includes(code));

        const selectedPoolKeys = allIndividualSelected && allPoolCodes.length !== 1
            ? [POOL_ALL_KEY, ...selectedPoolCodes]
            : selectedPoolCodes;

        let dropdownOptions: IDropdownOption[] = [
            { key: POOL_ALL_KEY, text: POOL_ALL_LABEL }
        ];
        sourcePools
            .forEach(x => dropdownOptions.push({ key: x.code, text: x.code, data: x.code }));

        const onRenderPoolTitle = (options: IDropdownOption[]): JSX.Element => {
            if (!options || options.length === 0) return <span></span>;
            if (options.some(o => o.key === POOL_ALL_KEY)) {
                return <span>{POOL_ALL_LABEL}</span>;
            }
            return <span>{options.map(o => o.text).join(', ')}</span>;
        };

        const renderPostBatch = (): JSX.Element => {
            if (dataSource === DataSource.Intray) {
                return <WorkBatchInput
                    label={viewData[ADJUSTMENT_BATCH_ID_STATE_KEY]}
                    fieldName={ADJUSTMENT_BATCH_ID_STATE_KEY}
                    value={batchId}
                    changeData={changeData}
                    postButtonLabel={adjustmentBatchOperationInProgress ? "Posting..." : viewData[POST_ADJUSTMENT_BATCH_STATE_KEY]}
                    postButtonAction={() => postAdjustmentBatch(batchId)}
                    matchButtonLabel={null}
                    matchButtonAction={() => true}
                    readOnly={isReadonly}
                    isEnabled={!changesMade && !saveInProgress && !adjustmentBatchOperationInProgress && !isReadonly}
                />
            }
            else {
                return null;
            }
        }

        if (loaded) {
            return (
                <div>
                    <div className="">
                        <div className="row">
                            <div className="col-md-12">
                                <h1>Adjustment Information</h1>
                            </div>
                        </div>
                        <div className="row">
                            <SizedDropdownDataInput
                                label={viewData[ADJUSTMENT_SOURCE_DISTRIBUTION_STATE_KEY]}
                                fieldName={ADJUSTMENT_SOURCE_DISTRIBUTION_STATE_KEY}
                                changeData={changeData}
                                value={sourceDistribution || ''}
                                isHidden={!viewData[ADJUSTMENT_SOURCE_DISTRIBUTION_STATE_KEY]}
                                readOnly={isReadonly}
                                options={sourceDistributions}
                                allowNull={false}
                                isMandatory={isAdjustmentGeneralFieldMandatory(ADJUSTMENT_SOURCE_DISTRIBUTION_STATE_KEY)}
                            />
                            <div className={REPERTOIRE_PAGE_FORM_ITEM_SIZE}>
                                <Dropdown
                                    label={viewData[ADJUSTMENT_SOURCE_POOL_STATE_KEY]}
                                    selectedKeys={selectedPoolKeys}
                                    options={dropdownOptions}
                                    multiSelect={true}
                                    onChange={multiSelectChangeData}
                                    onRenderTitle={onRenderPoolTitle}
                                    placeholder={"No Pools selected."}
                                    disabled={!(dataSource === DataSource.Intray)}
                                />
                            </div>
 
                            <SizedDropdownDataInput
                                label={viewData[ADJUSTMENT_SOURCE_SEGMENT_STATE_KEY]}
                                fieldName={ADJUSTMENT_SOURCE_SEGMENT_STATE_KEY}
                                changeData={changeData}
                                value={sourceSegment}
                                isHidden={!viewData[ADJUSTMENT_SOURCE_SEGMENT_STATE_KEY]}
                                readOnly={isReadonly || (sourcePool?.length || 0) > 1}
                                options={sourceSegments}
                                allowNull={true}
                            />
                        </div>
                        <div className="row">
                            <SizedDropdownDataInput
                                label={viewData[ADJUSTMENT_ADJUSTMENT_DISTRIBUTION_STATE_KEY]}
                                fieldName={ADJUSTMENT_ADJUSTMENT_DISTRIBUTION_STATE_KEY}
                                changeData={changeData}
                                value={adjustmentDistribution || ''}
                                isHidden={!viewData[ADJUSTMENT_ADJUSTMENT_DISTRIBUTION_STATE_KEY]}
                                readOnly={isReadonly}
                                options={adjustmentDistributions}
                                allowNull={false}
                                isMandatory={isAdjustmentGeneralFieldMandatory(ADJUSTMENT_ADJUSTMENT_DISTRIBUTION_STATE_KEY)}
                            />
                            <SizedDropdownDataInput
                                label={viewData[ADJUSTMENT_ADJUSTMENT_POOL_STATE_KEY]}
                                fieldName={ADJUSTMENT_ADJUSTMENT_POOL_STATE_KEY}
                                changeData={changeData}
                                value={adjustmentPool}
                                isHidden={!viewData[ADJUSTMENT_ADJUSTMENT_POOL_STATE_KEY]}
                                readOnly={isReadonly}
                                options={adjustmentPools}
                                allowNull={true}
                            />
                            <SizedDropdownDataInput
                                label={viewData[ADJUSTMENT_ADJUSTMENT_SEGMENT_STATE_KEY]}
                                fieldName={ADJUSTMENT_ADJUSTMENT_SEGMENT_STATE_KEY}
                                changeData={changeData}
                                value={adjustmentSegment}
                                isHidden={!viewData[ADJUSTMENT_ADJUSTMENT_SEGMENT_STATE_KEY]}
                                readOnly={isReadonly}
                                options={adjustmentSegments}
                                allowNull={true}
                            />
                        </div>
                        <div className="row">
                            <SizedDateDataInput
                                label={viewData[ADJUSTMENT_DATE_STATE_KEY]}
                                fieldName={ADJUSTMENT_DATE_STATE_KEY}
                                changeData={changeData}
                                value={adjustmentDate}
                                isHidden={!viewData[ADJUSTMENT_DATE_STATE_KEY]}
                                readOnly={isReadonly}
                            />
                            <SizedDropdownDataInput
                                label={viewData[ADJUSTMENT_STATUS_STATE_KEY]}
                                fieldName={ADJUSTMENT_STATUS_STATE_KEY}
                                changeData={changeData}
                                value={adjustmentStatus || ''}
                                isHidden={!viewData[ADJUSTMENT_STATUS_STATE_KEY]}
                                readOnly={isReadonly}
                                options={getLookupValues(ADJUSTMENT_STATUS_LOOKUP, lookupValues)}
                                allowNull={false}
                                isMandatory={isAdjustmentGeneralFieldMandatory(ADJUSTMENT_STATUS_STATE_KEY)}
                            />
                            <SizedDropdownDataInput
                                label={viewData[ADJUSTMENT_CATEGORY_STATE_KEY]}
                                fieldName={ADJUSTMENT_CATEGORY_STATE_KEY}
                                changeData={changeData}
                                value={adjustmentCategory || ''}
                                isHidden={!viewData[ADJUSTMENT_CATEGORY_STATE_KEY]}
                                readOnly={isReadonly}
                                options={getLookupValues(ADJUSTMENT_CATEGORY_LOOKUP, lookupValues)}
                                allowNull={false}
                                isMandatory={isAdjustmentGeneralFieldMandatory(ADJUSTMENT_CATEGORY_STATE_KEY)}
                            />
                        </div>
                        <div className="row">
                            <SizedTextDataInput
                                label={viewData[ADJUSTMENT_NARRATIVE_STATE_KEY]}
                                fieldName={ADJUSTMENT_NARRATIVE_STATE_KEY}
                                changeData={changeData}
                                value={narrative}
                                isHidden={!viewData[ADJUSTMENT_NARRATIVE_STATE_KEY]}
                                readOnly={isReadonly}
                                textArea={true}
                            />
                        </div>
                        <If condition={dataSource === DataSource.Intray}>
                            <div className="row">
                                {renderPostBatch()}
                            </div>
                        </If>
                    </div>
                </div>
            );
        }
        return <div />;
}

export default AdjustmentMaintenanceGeneralDataView;